import React, { useRef } from "react";
import type { FC } from "react";
import { animated } from "@react-spring/web";
import cl from "classnames";

import { useSlider } from "./hooks";
import ResponsiveImage from "ui/Image";
import Paggination, { PagesDotes } from "./Paggination";

import css from "./ImageSlider.module.scss";
import HotKeysHandler from "./HotKeysHandler";
import Link from "./Link";
import { useClientOnly } from "@hooks/useClientOnly";

type SlideType =
    | string
    | {
          src: string;
          title: React.ReactNode;
          alt: string;
      }
    | {
          src: string;
          title: string;
          alt?: string;
      };

type Props = {
    images: SlideType[];
    // Example: [{ media: "(max-width: 992)", width: 498}]
    sources: {
        width?: number;
        height?: number;
        media: string;
    }[];
    // Default selected image
    selectedImage?: number;
    isContainObjectFit?: boolean;
    // Active means that the slider will listen arrows keys globaly on document
    // Usualy for modal window
    isActive?: boolean;
    // Hide paddination buttons and dots and show them only on hover
    isHiddenControls?: boolean;
    onBackgroundClick?: () => void;
    link?: React.AnchorHTMLAttributes<HTMLAnchorElement>;
    lazy?: boolean;
};

const AnimatedImage = animated(ResponsiveImage);

const ImageSlider: FC<Props> = ({
    images,
    sources,
    selectedImage = 0,
    isContainObjectFit = false,
    isActive = false,
    isHiddenControls = false,
    onBackgroundClick,
    link,
    lazy = false,
}) => {
    const isClient = useClientOnly();
    const slideRef = useRef<HTMLDivElement>(null);
    const {
        slides,
        gesturesProps,
        handleNext,
        handlePrevious,
        index: slideIndex,
    } = useSlider(images.length, slideRef, selectedImage, isContainObjectFit);
    const slidesWithImage = slides.map((slide) => ({
        ...slide,
        image: images[slide.index],
    }));


    const handleBackgroundClick = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        if (e.target === e.currentTarget) {
            onBackgroundClick?.();
        }
    };

    const currentElement = images[slideIndex];
    const slideTitle =
        typeof currentElement !== "string" ? currentElement.title : "";
    const hasTitle = images.find(
        (slide) => typeof slide !== "string" && !!slide.title
    );

    return (
        <div className={cl(css.container, { [css.withTitle]: hasTitle })}>
            <Link {...link}>
                {slidesWithImage.map(
                    ({ image, x, index, isPrevious, isNext }) => {
                        if (!isClient && (isPrevious || isNext)) {
                            return null;
                        }
                        return (
                            <animated.div
                                key={index}
                                ref={slideRef}
                                className={css.slide}
                                {...gesturesProps()}
                                style={{ x }}
                                onClick={handleBackgroundClick}
                            >
                                <AnimatedImage
                                    src={
                                        // @ts-ignore
                                        image.src ?? image
                                    }
                                    sources={sources}
                                    className={cl(
                                        css.image,
                                        isContainObjectFit
                                            ? css.imageContain
                                            : css.imageCover,
                                        {
                                            [css.previousImage]: isPrevious,
                                            [css.nextImage]: isNext,
                                        }
                                    )}
                                    alt={
                                        // @ts-ignore
                                        image?.alt ?? image?.title ?? ""
                                    }
                                    loading={lazy ? "lazy" : undefined}
                                />
                            </animated.div>
                        );
                    }
                )}
            </Link>

            {!!hasTitle && (
                <div className={css.title} key={slideIndex}>
                    {slideTitle}
                </div>
            )}
            {images.length && (
                <Paggination
                    isHiddenControls={isHiddenControls}
                    handlePrevious={handlePrevious}
                    handleNext={handleNext}
                />
            )}

            {!isHiddenControls && (
                <PagesDotes count={images.length} current={slideIndex} />
            )}
            {isActive && (
                <HotKeysHandler
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                />
            )}
        </div>
    );
};

export default ImageSlider;
